import { SlateValue2MustacheV2 } from 'components/TemplateEditor/mustacheUtils';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import { difference } from 'lodash-es';
import { useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { regionLanguageMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import { isValidItem } from 'views/Aide/Scenario/NewPolicy/ContentItemListField';
import { getSlateContent } from 'views/Aide/Scenario/PolicyForm';
import { mergePartialValue } from 'views/Aide/Scenario/PolicyForm/ExecuteContextsFieldV2';
import { translateTemplate } from 'views/Aide/Scenario/PolicyForm/TranslateTool';
import { getOrderOverview } from 'services/api/dashboard';
import dayjs from 'dayjs';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

export type FV = {
  repurchasePeriodDays?: number;
  contexts: Knowledge.Policy.KnowledgeScenarioPolicy[];
};

function transformItemHighlights(
  scenarioType: Knowledge.Policy.ScenarioType,
  itemHighlights: Knowledge.Activity.ItemHighlight[] = [],
) {
  const itemTransformer = (item: Knowledge.Activity.Item) => {
    const { platform, region, itemInternalId, spuId, shopId, storeId, title, imageUrl } = item;
    return {
      platform,
      region,
      itemId: itemInternalId,
      spuId,
      externalStoreId: shopId,
      internalStoreId: storeId,
      itemName: title,
      imageUrls: [imageUrl],
    };
  };

  const hasHighlight = itemHighlights.some(({ highlight }) => highlight);
  const allHighlight = hasHighlight && itemHighlights.every(({ highlight }) => highlight);

  const transformedItemHighlights = itemHighlights.reduce(
    (res, { highlight, appliedItems }) => {
      if (!highlight) {
        return res;
      }

      const applyItems = appliedItems.map(itemTransformer);

      const target = res.find((_) => _.itemHighlight === highlight);

      if (target) {
        target.applyItems = [...(target.applyItems || []), ...applyItems];
      } else {
        res.unshift({
          itemHighlight: highlight,
          applyItems,
        });
      }

      return res;
    },
    (['ORDER_REMINDER', 'PAYMENT_REMINDER'].includes(scenarioType) ? allHighlight : hasHighlight)
      ? []
      : [
          {
            itemHighlight: '',
            applyItems: itemHighlights.reduce<Knowledge.Policy.PolicyApplyItem[]>(
              (res, { appliedItems }) => {
                appliedItems && res.push(...appliedItems.map(itemTransformer));
                return res;
              },
              [],
            ),
          },
        ],
  );

  return transformedItemHighlights;
}

export const newScenarioTypes = [
  'INITIATIVE_RECENT_CONSULT_ACTIVITY_TIME_NO_ORDER',
  'INITIATIVE_HIGH_REPURCHASE',
  'INITIATIVE_ACTIVITY_POTENTIAL',
  'INITIATIVE_HIGH_VALUE',
  'INITIATIVE_OTHER',
] as const;

export type SendPointScenariosMap = Record<
  Knowledge.Policy.ActivitySendPoint,
  Knowledge.Policy.ScenarioType[]
>;

export function getSendPointScenariosMap(
  activity: Pick<
    Knowledge.Activity.Detail,
    'activityType' | 'activityPrepareFrom' | 'activityPrepareTo' | 'stores' | 'promotionType'
  >,
  newScenario?: boolean,
) {
  const { activityType, activityPrepareFrom, activityPrepareTo, stores, promotionType } = activity;

  const warmUp = Boolean(activityPrepareFrom && activityPrepareTo);

  const effectiveScenarios: Knowledge.Policy.ScenarioType[] = ['WELCOME_REPLY', 'ORDER_REMINDER'];

  if (
    !['VOUCHER', 'MULTI_PACK_DISCOUNT'].includes(promotionType) &&
    !['VOUCHER_GIFT_ACTIVITY', 'MULTI_PACK_DISCOUNT_ACTIVITY'].includes(activityType)
  ) {
    effectiveScenarios.push('PAYMENT_REMINDER');
  }

  effectiveScenarios.push('CONFIRM_TRADE', 'SIGN_REMINDER');

  const hasShopeeStore = stores.some(({ platform }) => platform === 'SHOPEE');

  if (hasShopeeStore) {
    effectiveScenarios.push('POSITIVE_REVIEW_CARE');
  }

  if (newScenario) {
    effectiveScenarios.push(...newScenarioTypes);
  }

  const warmUpScenarios: Knowledge.Policy.ScenarioType[] = [];
  if (warmUp) {
    warmUpScenarios.push('WELCOME_REPLY', 'CONFIRM_TRADE', 'SIGN_REMINDER');
    if (hasShopeeStore) {
      warmUpScenarios.push('POSITIVE_REVIEW_CARE');
    }
    if (newScenario) {
      warmUpScenarios.push(...newScenarioTypes);
    }
  }

  const sendPointScenariosMap: Record<
    Knowledge.Policy.ActivitySendPoint,
    Knowledge.Policy.ScenarioType[]
  > =
    activityType !== 'LIVE_STREAMING_ACTIVITY'
      ? {
          EFFECTIVE: effectiveScenarios,
          WARM_UP: warmUpScenarios,
          EFFECTIVE_IN_QUANTUM: [],
          EFFECTIVE_NOT_IN_QUANTUM: [],
        }
      : {
          EFFECTIVE_IN_QUANTUM: effectiveScenarios.filter((_) => _ !== 'CONFIRM_TRADE'),
          EFFECTIVE_NOT_IN_QUANTUM: effectiveScenarios.filter(
            (_) => _ !== 'ORDER_REMINDER' && _ !== 'PAYMENT_REMINDER',
          ),
          WARM_UP: warmUpScenarios,
          EFFECTIVE: [],
        };

  return sendPointScenariosMap;
}

export function createDefaultContexts(
  platforms: General.PlatformUpperCase[],
  regions: General.UpperCaseRegion[],
  activity: Knowledge.Activity.Detail,
  scenarioConfigs: Knowledge.Policy.ScenarioConfig[],
  newScenario?: boolean,
) {
  const createRegionPolicy = (
    region: General.UpperCaseRegion,
    activitySendPoint: Knowledge.Policy.ActivitySendPoint,
    scenarioType: Knowledge.Policy.ScenarioType,
  ): Knowledge.Policy.RegionPolicy => {
    const { activityType, itemHighlights } = activity;
    const scenarioConfigType = `${
      activityType === 'LIVE_STREAMING_ACTIVITY' ? 'LIVE_STREAMING_ACTIVITY_' : ''
    }${scenarioType}_${activitySendPoint}`;
    const target = scenarioConfigs.find(({ scenario }) => scenario === scenarioConfigType);
    const enable = target ? target.enable : true;
    const transformedItemHighlights = transformItemHighlights(scenarioType, itemHighlights);
    return {
      region,
      platformPolicyList: transformedItemHighlights.map(({ itemHighlight, applyItems }) => ({
        platforms,
        enable,
        executeContext: {
          content: '',
          translatedContent: '',
        },
        itemHighlight,
        applyItems: applyItems?.filter((_) => _.region === region),
      })),
    };
  };

  const createScenarioPolicy = (
    activitySendPoint: Knowledge.Policy.ActivitySendPoint,
    scenarioType: Knowledge.Policy.ScenarioType,
  ) => {
    return {
      activitySendPoint,
      scenarioType,
      regionPolicyList: regions.map((region) =>
        createRegionPolicy(region, activitySendPoint, scenarioType),
      ),
    };
  };

  const sendPointScenariosMap = getSendPointScenariosMap(activity, newScenario);

  return Object.keys(sendPointScenariosMap)
    .map((activitySendPoint) => {
      return sendPointScenariosMap[activitySendPoint].map((scenarioType) =>
        createScenarioPolicy(activitySendPoint, scenarioType),
      );
    })
    .flat();
}

export function useGetTitle(language?: General.Language) {
  const { t: _t } = useTranslate();

  const t = useCallback(
    (key: string, others?: any) => _t(key, { language, ...others }),
    [_t, language],
  );

  const getSendPointLabel = useCallback(
    (sendPoint: Knowledge.Policy.ActivitySendPoint, isLive: boolean) => {
      switch (sendPoint) {
        case 'EFFECTIVE':
          return t('活动生效期内');
        case 'WARM_UP':
          return isLive ? t('直播预热期内') : t('活动预热期内');
        case 'EFFECTIVE_IN_QUANTUM':
          return t('直播当天直播中');
        case 'EFFECTIVE_NOT_IN_QUANTUM':
          return t('直播当天直播前');
      }
    },
    [t],
  );

  const getScenarioTypeLabel = useCallback(
    (
      scenarioType: Knowledge.Policy.ScenarioType,
      activity?: Pick<Knowledge.Activity.Detail, 'items'>,
    ) => {
      const { items = [] } = activity || {};

      const itemsSelected = items.length > 0;

      switch (scenarioType) {
        case 'WELCOME_REPLY':
          return t('aide.进店欢迎语');
        case 'ORDER_REMINDER':
          return itemsSelected
            ? t('买家咨询活动商品后一定时间未下单')
            : t('买家咨询后一定时间未下单');
        case 'PAYMENT_REMINDER':
          return itemsSelected
            ? t('买家下单活动商品后一定时间未付款')
            : t('买家下单后一定时间未付款');
        case 'CONFIRM_TRADE':
          return t('买家购买商品后');
        case 'SIGN_REMINDER':
          return t('买家签收商品时');
        case 'POSITIVE_REVIEW_CARE':
          return t('买家对已有订单给出好评');
        case 'INITIATIVE_RECENT_CONSULT_ACTIVITY_TIME_NO_ORDER':
          return t('近期咨询活动商品后未下单的买家');
        case 'INITIATIVE_HIGH_REPURCHASE':
          return t('高复购意愿买家');
        case 'INITIATIVE_ACTIVITY_POTENTIAL':
          return t('活动商品高意向买家');
        case 'INITIATIVE_HIGH_VALUE':
          return t('高价值买家');
        case 'INITIATIVE_OTHER':
          return t('其他可触达买家');
      }
    },
    [t],
  );

  const getTitle = useCallback(
    (
      sendPoint: Knowledge.Policy.ActivitySendPoint,
      scenarioType: Knowledge.Policy.ScenarioType,
      activity?: Pick<Knowledge.Activity.Detail, 'activityType' | 'items'>,
    ) => {
      const scenarioTypeLabel = getScenarioTypeLabel(scenarioType, activity);
      const sendPointTypeLabel = getSendPointLabel(
        sendPoint,
        activity?.activityType === 'LIVE_STREAMING_ACTIVITY',
      );
      return `${scenarioTypeLabel}——${sendPointTypeLabel}`;
    },
    [getScenarioTypeLabel, getSendPointLabel],
  );

  return {
    t: _t,
    getTitle,
    getSendPointLabel,
    getScenarioTypeLabel,
  };
}

export function getContextsRegions(contexts: Knowledge.Policy.KnowledgeScenarioPolicy[]) {
  return contexts.reduce<General.UpperCaseRegion[]>((res, { regionPolicyList }) => {
    return regionPolicyList.reduce((res, { region }) => {
      if (!res.includes(region)) {
        res.push(region);
      }
      return res;
    }, res);
  }, []);
}

export function getPlatformsByStores(stores: Knowledge.Activity.Store[]) {
  return stores.reduce<General.PlatformUpperCase[]>((res, { platform }) => {
    if (!res.includes(platform)) {
      res.push(platform);
    }
    return res;
  }, []);
}

export function getRegionsByStores(stores: Knowledge.Activity.Store[]) {
  const regionCountMap = stores.reduce<Partial<Record<General.UpperCaseRegion, number>>>(
    (res, { region }) => {
      res[region] = (res[region] || 0) + 1;
      return res;
    },
    {},
  );

  return Object.keys(regionCountMap).sort(
    (a, b) => (regionCountMap[b] as any) - (regionCountMap[a] as any),
  );
}

export function patchContextsFormRemainRegions(
  contexts: Knowledge.Policy.KnowledgeScenarioPolicy[],
  remainRegions: General.UpperCaseRegion[],
  activity: Knowledge.Activity.Detail,
) {
  const { itemHighlights } = activity;

  return contexts.reduce<Promise<typeof contexts>>((res, cur) => {
    return res.then((res) => {
      const { activitySendPoint, scenarioType, regionPolicyList = [] } = cur;

      const [{ platformPolicyList = [] } = {}] = regionPolicyList;

      const transformedItemHighlights = transformItemHighlights(scenarioType, itemHighlights);

      return remainRegions
        .reduce((res, region) => {
          return res.then((res) => {
            return platformPolicyList
              .reduce<Promise<typeof platformPolicyList>>((res, cur) => {
                const {
                  platforms,
                  executeContext: { content },
                  itemHighlight,
                } = cur;

                const applyItems = transformedItemHighlights
                  .find((_) => _.itemHighlight === (itemHighlight || ''))
                  ?.applyItems.filter((_) => _.region === region);

                return res.then((res) => {
                  return getActivityMustacheTranslate(
                    content,
                    region,
                    activity,
                    scenarioType,
                    platforms,
                    activitySendPoint,
                    applyItems,
                  ).then((mustache) => {
                    res.push({
                      ...cur,
                      executeContext: {
                        content: mustache,
                        translatedContent: '',
                      },
                      applyItems: applyItems?.filter((_) => _.region === region),
                    });
                    return res;
                  });
                });
              }, Promise.resolve([]))
              .then((platformPolicyList) => {
                res.push({
                  region,
                  platformPolicyList,
                });
                return res;
              });
          });
        }, Promise.resolve([...regionPolicyList]))
        .then((regionPolicyList) => {
          res.push({
            activitySendPoint,
            scenarioType,
            regionPolicyList,
          });
          return res;
        });
    });
  }, Promise.resolve([]));
}

export function getActivityMustacheTranslate(
  content: string,
  region: General.UpperCaseRegion,
  activity: Knowledge.Activity.Detail,
  scenarioType: Knowledge.Policy.ScenarioType,
  platforms: General.PlatformUpperCase[],
  sendPoint: Knowledge.Policy.ActivitySendPoint,
  applyItems?: Knowledge.Policy.PolicyApplyItem[],
) {
  const template = getSlateContent(content);

  const shouldPatchItem = Boolean(
    getPolicyContentItems(
      activity,
      scenarioType,
      region,
      sendPoint,
      platforms.length === 1 ? platforms[0] : undefined,
      applyItems,
    )?.length,
  );
  const shouldPatchVoucher = !!getPolicyContentVoucher(activity, scenarioType);

  return translateTemplate(template, regionLanguageMap[toLowerCase(region)], [], undefined, {
    from: 'gpt4TranslateTool',
  }).then((content) => {
    let value = mergePartialValue(
      {
        content: template,
        translatedContent: getSlateContent(''),
      },
      {
        content: content,
        translatedContent: getSlateContent(''),
      },
      'text',
    );

    if (shouldPatchItem) {
      value = mergePartialValue(
        value,
        {
          content: [{ type: CustomSlateNodeType.ITEM }],
          translatedContent: getSlateContent(''),
        },
        'item',
      );
    }
    if (shouldPatchVoucher) {
      value = mergePartialValue(
        value,
        {
          content: [{ type: CustomSlateNodeType.VOUCHER }],
          translatedContent: getSlateContent(''),
        },
        'voucher',
      );
    }

    const mustache = `#mustache#${SlateValue2MustacheV2(value.content)}`;

    return mustache;
  });
}

export function getPolicyContentItems(
  activity: Knowledge.Activity.Detail,
  scenarioType: Knowledge.Policy.ScenarioType,
  region: General.UpperCaseRegion,
  sendPoint: Knowledge.Policy.ActivitySendPoint,
  platform?: General.PlatformUpperCase,
  applyItems?: Knowledge.Policy.PolicyApplyItem[],
) {
  if (
    scenarioType === 'ORDER_REMINDER' ||
    scenarioType === 'PAYMENT_REMINDER' ||
    (sendPoint === 'WARM_UP' &&
      scenarioType !== 'WELCOME_REPLY' &&
      !newScenarioTypes.includes(scenarioType))
  ) {
    return [];
  }

  const { activityType, items, recommendItems } = activity;

  return (
    applyItems?.length
      ? applyItems.map(
          ({
            platform,
            region,
            spuId,
            itemId,
            internalStoreId,
            externalStoreId,
            itemName,
            imageUrls: [imageUrl] = [],
          }) => ({
            platform,
            region,
            storeId: internalStoreId,
            shopId: externalStoreId,
            spuId,
            itemInternalId: itemId,
            title: itemName,
            imageUrl,
          }),
        )
      : activityType === 'RECOMMEND_ACTIVITY'
      ? items
      : recommendItems
  )?.filter((item) => isValidItem(item, region, platform));
}

/** 话术随机优惠券 */
export function getPolicyContentVoucher(
  activity: Knowledge.Activity.Detail,
  scenarioType: Knowledge.Policy.ScenarioType,
) {
  // 除了催付款以外的全部话术
  if (scenarioType === 'PAYMENT_REMINDER') {
    return;
  }
  return activity?.voucherList?.[0];
}

export function text2mustache(text: string, options?: { item?: boolean; voucher?: boolean }) {
  const item = options?.item ?? false;
  const voucher = options?.voucher ?? false;

  return `#mustache#${text
    .split('{{/concept}}')
    .map((textWithConcept) => {
      const [text, concept] = textWithConcept.split('{{#concept}}');

      if (!text) {
        return '';
      }

      const textTag = `{{#text}}${encodeURIComponent(text)}{{/text}}`;

      if (!concept) {
        return textTag;
      }

      const conceptLabel =
        concept === 'Product_Discount'
          ? '商品折扣'
          : concept === 'Live_Time_Period'
          ? '直播时间段'
          : concept;

      return `${textTag}{{#concept}}${encodeURIComponent(conceptLabel)}{{/concept}}`;
    })
    .join('')}${item ? '{{item}}' : ''}${voucher ? '{{voucher}}' : ''}`;
}

export function policysGroupbyPlatforms(policys: Knowledge.Policy.PlatformPolicy[]) {
  const isSamePlatforms = (
    platforms: General.PlatformUpperCase[],
    another: General.PlatformUpperCase[],
  ) => !difference(platforms, another).length && !difference(another, platforms).length;

  return policys.reduce(
    (res, policy, index) => {
      const target = res.find((list) =>
        list.some(({ policy: { platforms } }) => isSamePlatforms(platforms, policy.platforms)),
      );
      const policyInfo = { index, policy };

      if (target) {
        target.push(policyInfo);
      } else {
        res.push([policyInfo]);
      }

      return res;
    },
    [] as {
      index: number;
      policy: Knowledge.Policy.PlatformPolicy;
    }[][],
  );
}

export function useGetNewScenario() {
  const { whiteListConfig: { LAO_KE_ZHAO_HUI = false } = {} } = useOrgConfigStateValue();

  return useCallback(
    (activity: Pick<Knowledge.Activity.Detail, 'stores'>) => {
      if (LAO_KE_ZHAO_HUI) {
        return Promise.resolve(true);
      }

      return getOrderOverview({
        startTime: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
        endTime: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
        storeIds: activity.stores.map(({ storeId }) => storeId) as any,
      })
        .then((res) => {
          return (res.orderOutline?.orderCount || 0) > 500;
        })
        .catch(() => false);
    },
    [LAO_KE_ZHAO_HUI],
  );
}
